import posState from "./posState";

const {
  state,
  hasTerminal,
  hasActiveSession
} = posState;

// ============================================================
// POS RUNTIME GUARD
//
// Cart operations require:
//
// 1. A selected runtime terminal.
// 2. An active POS cashier session.
//
// Terminal activation/deactivation remains an admin operation.
// ============================================================

function requireActiveSession() {
  if (
    !hasTerminal.value
  ) {
    throw new Error(
      "A POS terminal must be selected before modifying the cart."
    );
  }

  if (
    !hasActiveSession.value
  ) {
    throw new Error(
      "An active POS session is required before modifying the cart."
    );
  }

  return true;
}

// ============================================================
// CART MUTATION GUARD
//
// Once payment processing has started, the cart must not
// change underneath the active payment transaction.
// ============================================================

function requireCartMutable() {
  requireActiveSession();

  if (
    state.processingPayment
  ) {
    throw new Error(
      "The cart cannot be modified while a payment is being processed."
    );
  }

  const paymentStatus =
    String(
      state.paymentStatus ??
      ""
    )
      .trim()
      .toUpperCase();

  if (
    paymentStatus === "PENDING" ||
    paymentStatus === "PROCESSING"
  ) {
    throw new Error(
      "The cart cannot be modified while a payment is pending."
    );
  }

  return true;
}

// ============================================================
// PRODUCT ID
// ============================================================

function getProductId(
  product
) {
  return (
    product?.itemId
    ??
    product?.id
    ??
    product?.productId
    ??
    null
  );
}

// ============================================================
// ID COMPARISON
// ============================================================

function idsMatch(
  left,
  right
) {
  if (
    left === null ||
    left === undefined ||
    right === null ||
    right === undefined
  ) {
    return false;
  }

  return (
    String(left) ===
    String(right)
  );
}

// ============================================================
// UNIT PRICE
// ============================================================

function getUnitPrice(
  product
) {
  const value =
    Number(
      product?.unitPrice
      ??
      product?.sellingPrice
      ??
      product?.price
      ??
      0
    );

  return Number.isFinite(
    value
  )
    ? value
    : 0;
}

// ============================================================
// ADD PRODUCT
// ============================================================

function addProduct(
  product,
  quantity = 1
) {
  requireCartMutable();

  if (!product) {
    throw new Error(
      "A product is required."
    );
  }

  const itemId =
    getProductId(
      product
    );

  if (
    itemId === null ||
    itemId === undefined ||
    itemId === ""
  ) {
    throw new Error(
      "The selected product does not contain an item ID."
    );
  }

  const numericQuantity =
    Number(
      quantity
    );

  if (
    !Number.isFinite(
      numericQuantity
    )
    ||
    numericQuantity <= 0
  ) {
    throw new Error(
      "Product quantity must be greater than zero."
    );
  }

  const existing =
    state.cart.find(
      item =>
        idsMatch(
          item.itemId,
          itemId
        )
    );

  if (
    existing
  ) {
    existing.quantity =
      Number(
        existing.quantity ??
        0
      )
      +
      numericQuantity;

    invalidateSale();

    return existing;
  }

  const cartItem = {
    itemId,

    itemCode:
      product.itemCode
      ??
      product.code
      ??
      product.sku
      ??
      "",

    description:
      product.itemName
      ??
      product.description
      ??
      product.name
      ??
      "Product",

    image:
      product.image
      ??
      product.imageUrl
      ??
      product.imagePath
      ??
      null,

    unitPrice:
      getUnitPrice(
        product
      ),

    quantity:
      numericQuantity,

    source:
      product
  };

  state.cart.push(
    cartItem
  );

  invalidateSale();

  return cartItem;
}

// ============================================================
// INCREASE QUANTITY
// ============================================================

function increaseQuantity(
  itemId
) {
  requireCartMutable();

  const item =
    findCartItem(
      itemId
    );

  if (!item) {
    return null;
  }

  item.quantity =
    Number(
      item.quantity ??
      0
    )
    +
    1;

  invalidateSale();

  return item;
}

// ============================================================
// DECREASE QUANTITY
// ============================================================

function decreaseQuantity(
  itemId
) {
  requireCartMutable();

  const item =
    findCartItem(
      itemId
    );

  if (!item) {
    return null;
  }

  const currentQuantity =
    Number(
      item.quantity ??
      0
    );

  if (
    currentQuantity <= 1
  ) {
    /*
     * We already passed requireCartMutable(),
     * so use the internal removal method rather than
     * calling public removeItem() and guarding twice.
     */

    removeItemInternal(
      itemId
    );

    invalidateSale();

    return null;
  }

  item.quantity =
    currentQuantity -
    1;

  invalidateSale();

  return item;
}

// ============================================================
// SET QUANTITY
// ============================================================

function setQuantity(
  itemId,
  quantity
) {
  requireCartMutable();

  const item =
    findCartItem(
      itemId
    );

  if (!item) {
    return null;
  }

  const numericQuantity =
    Number(
      quantity
    );

  if (
    !Number.isFinite(
      numericQuantity
    )
  ) {
    throw new Error(
      "Quantity must be a valid number."
    );
  }

  if (
    numericQuantity <= 0
  ) {
    removeItemInternal(
      itemId
    );

    invalidateSale();

    return null;
  }

  item.quantity =
    numericQuantity;

  invalidateSale();

  return item;
}

// ============================================================
// REMOVE ITEM
// ============================================================

function removeItem(
  itemId
) {
  requireCartMutable();

  const removed =
    removeItemInternal(
      itemId
    );

  if (!removed) {
    return false;
  }

  invalidateSale();

  return true;
}

// ============================================================
// INTERNAL REMOVE
//
// Assumes the caller has already performed any required
// cart/session/payment guards.
// ============================================================

function removeItemInternal(
  itemId
) {
  const index =
    state.cart.findIndex(
      item =>
        idsMatch(
          item.itemId,
          itemId
        )
    );

  if (
    index < 0
  ) {
    return false;
  }

  state.cart.splice(
    index,
    1
  );

  return true;
}

// ============================================================
// FIND CART ITEM
// ============================================================

function findCartItem(
  itemId
) {
  return (
    state.cart.find(
      item =>
        idsMatch(
          item.itemId,
          itemId
        )
    )
    ??
    null
  );
}

// ============================================================
// CLEAR CART
// ============================================================

function clearCart() {
  requireCartMutable();

  if (
    state.cart.length === 0
  ) {
    return false;
  }

  state.cart.splice(
    0,
    state.cart.length
  );

  invalidateSale();

  return true;
}

// ============================================================
// FORCE CLEAR CART
//
// INTERNAL / SYSTEM RESET.
//
// Deliberately bypasses:
//
// - terminal requirement
// - session requirement
// - payment mutation guard
//
// Required by:
//
// - session close
// - terminal close
// - logout
// - resetTransaction()
// - resetAll()
//
// Do not connect this directly to normal cashier controls.
// ============================================================

function forceClearCart() {
  if (
    state.cart.length > 0
  ) {
    state.cart.splice(
      0,
      state.cart.length
    );
  }

  invalidateSale();
}

// ============================================================
// SALE INVALIDATION
//
// Any cart mutation makes the existing sale/checkout/payment
// state stale.
//
// The next checkout must therefore create/recalculate the
// authoritative backend sale again.
// ============================================================

function invalidateSale() {
  // ==========================================================
  // SALE
  // ==========================================================

  state.sale =
    null;

  state.creatingSale =
    false;

  // ==========================================================
  // CHECKOUT
  // ==========================================================

  state.checkout =
    null;

  state.checkoutDialog =
    false;

  state.checkingOut =
    false;

  // ==========================================================
  // PAYMENT
  // ==========================================================

  state.paymentMethod =
    null;

  state.paymentTransaction =
    null;

  state.paymentStatus =
    null;

  state.paymentError =
    null;

  state.processingPayment =
    false;

  // ==========================================================
  // STRIPE
  // ==========================================================

  state.stripeCheckout =
    null;

  state.stripeCheckoutSessionId =
    null;

  state.stripePaymentTransactionId =
    null;

  state.stripePolling =
    false;

  // ==========================================================
  // RECEIPT
  // ==========================================================

  state.receipt =
    null;

  // ==========================================================
  // GENERAL ERROR
  // ==========================================================

  state.error =
    null;
}

// ============================================================
// EXPORT
// ============================================================

export default {
  // ==========================================================
  // PRODUCT
  // ==========================================================

  addProduct,

  // ==========================================================
  // QUANTITY
  // ==========================================================

  increaseQuantity,
  decreaseQuantity,
  setQuantity,

  // ==========================================================
  // REMOVE / CLEAR
  // ==========================================================

  removeItem,
  clearCart,

  // ==========================================================
  // SYSTEM RESET ONLY
  // ==========================================================

  forceClearCart
};